import { useEffect, useState } from "react";
import { MDBBtn, MDBContainer } from "mdb-react-ui-kit";

import { useAuth } from "../../components/Auth";
import { CountCard } from "../../components";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { FilterIconHeader } from "../../components/FilterIconHeader";
import { SwitchForm } from "../../components/SwitchForm";
import {
    formatScreeningOutcomeHits,
    formatScreeningOutcomeHitsPlainText,
    ConductCheckReviewModal,
} from "./components";

import { getDataTableColumns } from "./columns";
import {
    convertISOToReadableDate,
    showErrorToast,
    extractUniqueScreeningOutcomeTypes,
} from "../../helpers";
import { exportConductCheckToExcel } from "./export";

import { conductCheckSignals } from "../../constants";
import { DataTable } from "stm-frontend-library";
import { getSelectedJournalIds } from "../../helpers/journals";
import {getConductCheckReports} from "../../helpers/conductCheck";

const ConductCheck = () => {
    const { profile } = useAuth();

    const [error, setError] = useState(false);

    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalHits, setTotalHits] = useState(0);
    const [filterSelection, setFilterSelection] = useState([]);
    const [selectedDataItem, setSelectedDataItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleRowClick = (index) => {
        setSelectedDataItem(filteredData[index]);
    };

    const handleCloseModal = () => {
        setSelectedDataItem(null);
    };

    const dataWithActions = filteredData?.map((element, index) => ({
        ...element,
        timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
        raw_hits: formatScreeningOutcomeHitsPlainText(element.screening_outcomes),
        hits: (
            <span
                dangerouslySetInnerHTML={{
                    __html: formatScreeningOutcomeHits(element.screening_outcomes),
                }}
            />
        ),
        manuscript_id: element.manuscript_id ?? "",
        clickEvent: () => handleRowClick(index),
    }));

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const selectedJournalIds = await getSelectedJournalIds();
                const { data: { manuscripts = [], total = 0 } = {} } =
                    await getConductCheckReports(selectedJournalIds);
                setAllData(manuscripts);
                setTotalHits(total);
                // Initialize filter with ALL enabled ConductCheck signals, but only pre-select those with hits
                const enabledConductCheckSignals = conductCheckSignals.filter(
                    (signal) => profile[`tool.${signal.value}`]
                );

                // Get signals that actually have hits in the data
                const signalsWithHits = extractUniqueScreeningOutcomeTypes(manuscripts);

                const initialFilter = Object.fromEntries(
                    enabledConductCheckSignals.map((signal) => [
                        signal.value,
                        signalsWithHits.includes(signal.value), // Only true if signal has hits
                    ])
                );
                setFilterSelection(initialFilter);
            } catch (error) {
                showErrorToast();
                setError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (Object.keys(filterSelection).length) {
            const filteredManuscripts = allData.filter((manuscript) =>
                manuscript.screening_outcomes.some(
                    (outcome) => filterSelection[outcome.type]
                )
            );
            setFilteredData(filteredManuscripts);
        } else {
            setFilteredData(allData);
        }
    }, [filterSelection, allData]);

    // Create options that include ALL enabled signals from profile, not just signals with hits
    const options = conductCheckSignals
        .filter((signal) => profile[`tool.${signal.value}`])
        .map((signal) => ({
            ...signal,
            value: signal.value.replace("conductCheck.", ""), // Remove the conductCheck. prefix
        }));

    const handleFilterSubmit = (values) => {
        // Add the conductCheck. prefix back to the values
        const valuesWithPrefix = {};
        Object.keys(values).forEach((key) => {
            valuesWithPrefix[`conductCheck.${key}`] = values[key];
        });

        setFilterSelection(valuesWithPrefix);
    };

    const handleFilterReset = () => {
        const resetValues = Object.fromEntries(
            Object.keys(filterSelection).map((type) => [type, true])
        );
        setFilterSelection(resetValues);
    };

    // Create initial values without the conductCheck. prefix for the form
    const formInitialValues = {};
    Object.keys(filterSelection).forEach((key) => {
        if (key.startsWith("conductCheck.")) {
            formInitialValues[key.replace("conductCheck.", "")] = filterSelection[key];
        }
    });

    const filterComponent = (
        <FilterIconHeader label="Hits" dropdownTitle="Signal Type Selection">
            <SwitchForm
                handleSubmit={handleFilterSubmit}
                handleReset={handleFilterReset}
                initialValues={formInitialValues}
                options={options}
            />
        </FilterIconHeader>
    );
    const tableColumns = getDataTableColumns(filterComponent);

    const handleExport = () => {
        exportConductCheckToExcel(dataWithActions, "ConductCheck");
    };

    return (
        <MDBContainer className="my-5">
            {selectedDataItem && (
                <ConductCheckReviewModal
                    contentData={selectedDataItem}
                    onClose={handleCloseModal}
                />
            )}
            {error && <ErrorMessage />}
            {!error && (
                <>
                    <div className="d-flex justify-content-center m-5">
                        <CountCard
                            count={totalHits}
                            title={"CONDUCTCHECK HITS"}
                            subtitle={"Screened manuscripts with hits"}
                        />
                    </div>
                    <div className="d-flex justify-content-end mb-3">
                        <MDBBtn
                            disabled={isLoading}
                            color="primary"
                            className="px-4"
                            onClick={handleExport}
                        >
                            Export
                        </MDBBtn>
                    </div>
                    <DataTable
                        columns={tableColumns}
                        rows={dataWithActions}
                        isLoading={isLoading}
                    />
                </>
            )}
        </MDBContainer>
    );
};

export default ConductCheck;
